import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/api';
import type { components } from '@/api/qraft/schema';
import { TitleWithQuestion } from '@/common/TitleWithQuestion';
import { WBSButton } from '@/common/WBSButton';
import StudentsTable from '@/tables/coordinator/StudentsTable';
import MyStudentsTable from '@/tables/MyStudentsTable';
import type { HelpPlacement } from '~/hooks/useHelpPlacements';
import { useIsCoordinatorGroup } from '~/hooks/useIsCoordinatorGroup';
import { GroupHeaderWrapper } from '~/styles/layoutElements';

/**
 * The help placement per group type, as legacy anchors it. Social and untyped
 * groups get none. Legacy's follow-up group variants live on
 * `follow-up-students.index.tsx`, keyed on the caller's profile type.
 */
const STUDENTS_HELP_PLACEMENT: Partial<
  Record<components['schemas']['GroupType'], HelpPlacement>
> = {
  STUDY_HELPER_STUDY_GROUP: 'students',
  CONGREGATION: 'students',
  CUSTOM_STUDY_GROUP: 'students',
};

export const Route = createFileRoute(
  '/_app/_dashboard/groups/$groupName/students/all-students/',
)({
  // The Study Helpers tab's drill-in. Legacy carries it in the URL fragment; a
  // validated search param does the same and accepts only this one key.
  validateSearch: (search): { study_helper_id?: number } => {
    const id = Number(search.study_helper_id);
    return {
      study_helper_id: Number.isInteger(id) && id > 0 ? id : undefined,
    };
  },
  component: StudentsScreen,
});

function StudentsScreen() {
  const { t } = useTranslation();
  const { groupName } = Route.useParams();
  const { study_helper_id: deepLinkedHelperId } = Route.useSearch();
  const navigate = useNavigate();

  // Read once and then cleared, as legacy does with the fragment: the first
  // request is filtered, the URL stops carrying it, and the panel owns the
  // value from then on, including when the user clears the Study Helper control
  // (`useWbsTableQuery`'s `seedParams` stops an emptied control falling back to
  // the seed). `replace: true` keeps the stripped URL out of history, so Back
  // leaves the screen instead of re-applying the filter.
  const [seededHelperId] = useState(() =>
    deepLinkedHelperId ? [deepLinkedHelperId] : undefined,
  );
  useEffect(() => {
    if (deepLinkedHelperId === undefined) return;
    void navigate({
      to: '/groups/$groupName/students/all-students/',
      params: { groupName },
      search: { study_helper_id: undefined },
      replace: true,
    });
  }, [deepLinkedHelperId, groupName, navigate]);

  // Group type alone, as in legacy: with the role included, a platform admin
  // (whose `viewer_role` is null) would get the wrong table for the group's
  // type.
  const isCoordinatorGroup = useIsCoordinatorGroup();
  const { data: groupData } = api.getGroupBySlug.useQuery({
    path: {
      group_slug: groupName,
    },
  });
  const groupId = groupData?.id || 0;

  const handleAddPostalStudent = () => {
    navigate({
      to: '/groups/$groupName/students/all-students/new/',
      params: { groupName },
    });
  };

  return (
    <>
      <GroupHeaderWrapper>
        <TitleWithQuestion
          title={t('lessons.students')}
          placement={
            groupData?.group_type
              ? STUDENTS_HELP_PLACEMENT[groupData.group_type]
              : undefined
          }
        />
        {/* Only on study helper groups, as in legacy; never congregations,
           custom study groups or follow-up groups. */}
        {groupData?.group_type === 'STUDY_HELPER_STUDY_GROUP' && (
          <WBSButton wide onClick={handleAddPostalStudent}>
            {t('actions.add-postal-student')}
          </WBSButton>
        )}
      </GroupHeaderWrapper>
      {isCoordinatorGroup ? (
        <StudentsTable
          groupId={groupId}
          groupType={groupData?.group_type ?? undefined}
          seededHelperId={seededHelperId}
        />
      ) : (
        // No `seededHelperId`: this table has no Study Helper control, and the
        // tab that emits the link exists only on congregations and custom study
        // groups.
        <MyStudentsTable groupId={groupId} />
      )}
    </>
  );
}
